<template>
  <div class="px-4 pt-5 pb-4 border-b border-gray-800">
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <MdiPackageVariantClosed class="w-4 h-4 text-teal-400 shrink-0" />
        <div class="section-label" style="margin-bottom:0">{{ $t('items.containedItems') }}</div>
        <span v-if="children.length" class="badge badge-muted badge-pill text-xs">{{ children.length }}</span>
      </div>
      <button class="btn ghost small" @click="showAddPanel = !showAddPanel">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3.5 h-3.5">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        {{ showAddPanel ? $t('common.cancel') : $t('common.add') }}
      </button>
    </div>

    <!-- ── Add panel ──────────────────────────────────────────────── -->
    <div v-if="showAddPanel" class="bg-[var(--color-surface)] border border-blue-900 rounded-xl p-4 mb-3">
      <!-- Tab toggle -->
      <div class="flex gap-2 mb-3">
        <button
          class="px-3 py-1.5 rounded-lg text-xs font-semibold border-none cursor-pointer transition-colors"
          :class="addMode === 'search' ? 'bg-blue-900 text-blue-200' : 'bg-transparent text-gray-400 hover:text-gray-200'"
          @click="addMode = 'search'"
        >{{ $t('items.searchExisting') }}</button>
        <button
          class="px-3 py-1.5 rounded-lg text-xs font-semibold border-none cursor-pointer transition-colors"
          :class="addMode === 'create' ? 'bg-blue-900 text-blue-200' : 'bg-transparent text-gray-400 hover:text-gray-200'"
          @click="addMode = 'create'"
        >{{ $t('items.createNew') }}</button>
      </div>

      <!-- Search existing -->
      <div v-if="addMode === 'search'" class="relative">
        <BaseFormField :label="$t('items.searchPlaceholder')">
          <BaseInput
            ref="searchInput"
            v-model="searchQuery"
            :placeholder="$t('items.searchPlaceholder')"
            @focus="searchOpen = true"
            @blur="onSearchBlur"
          />
        </BaseFormField>
        <div
          v-if="searchOpen && searchQuery.trim().length >= 2 && (searchLoading || searchResults.length > 0)"
          class="absolute left-0 right-0 top-full mt-1 bg-[var(--color-surface-2)] border border-gray-700 rounded-xl shadow-xl z-50 max-h-72 overflow-y-auto"
        >
          <div v-if="searchLoading" class="px-3 py-2.5 text-xs text-gray-500">{{ $t('common.loading') }}</div>
          <button
            v-for="result in searchResults"
            :key="result.id"
            class="flex items-start gap-2 w-full px-3 py-2.5 bg-transparent border-none text-left cursor-pointer transition-colors hover:bg-gray-800"
            :disabled="assigning"
            @mousedown.prevent="assignExisting(result)"
          >
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold text-gray-200">{{ result.displayName }}</div>
              <div v-if="result.sub" class="text-xs text-gray-500 truncate">{{ result.sub }}</div>
            </div>
            <span class="badge badge-muted text-[0.65rem] shrink-0">{{ result.typeLabel }}</span>
          </button>
        </div>
      </div>

      <!-- Create new -->
      <div v-if="addMode === 'create'">
        <BaseFormField :label="$t('itemMetadata.instanceType')" required>
          <BaseSelect v-model="newChild.item">
            <option value="" disabled>{{ $t('items.selectType') }}</option>
            <option v-for="it in itemTypes" :key="it.id" :value="it.id">{{ it.name }}</option>
          </BaseSelect>
        </BaseFormField>
        <BaseFormField :label="$t('common.area')" required>
          <BaseSelect v-model="newChild.area">
            <option value="" disabled>{{ $t('items.selectArea') }}</option>
            <option v-for="a in areas" :key="a.id" :value="a.id">{{ a.name }}</option>
          </BaseSelect>
        </BaseFormField>
        <div class="form-row">
          <BaseFormField :label="$t('itemView.assetTag')">
            <BaseInput v-model="newChild.assetTag" placeholder="ABC-001" maxlength="200" />
          </BaseFormField>
          <BaseFormField :label="$t('itemView.serialNumber')">
            <BaseInput v-model="newChild.serial_number" placeholder="SN12345" maxlength="200" />
          </BaseFormField>
        </div>
        <button
          class="btn primary small mt-2"
          :disabled="!newChild.item || !newChild.area || creatingChild"
          @click="createChild"
        >
          {{ creatingChild ? $t('common.creating') : $t('items.createAndAdd') }}
        </button>
      </div>
    </div>

    <!-- ── Children list ──────────────────────────────────────────── -->
    <EmptyState v-if="loadingChildren" :message="$t('common.loading')" />
    <EmptyState v-else-if="!children.length && !showAddPanel" :message="$t('items.noContainedItems')" />
    <div v-else class="list-stack">
      <div
        v-for="child in children"
        :key="child.id"
        class="data-card cursor-pointer"
        @click="goToChild(child)"
      >
        <div class="data-card__main">
          <div class="data-card__name">
            {{ child.item?.name ?? $t('itemMetadata.title') }}
            <span v-if="child.assetTag" class="text-xs text-gray-500 ml-1">{{ child.assetTag }}</span>
          </div>
          <div class="data-card__meta">
            <span v-if="child.serial_number" class="text-xs text-gray-500">SN: {{ child.serial_number }}</span>
            <span v-if="child.area" class="text-xs text-gray-500">{{ typeof child.area === 'object' ? child.area.name : child.area }}</span>
            <span
              class="badge badge-pill text-[0.6rem]"
              :class="child.status === 1 ? 'badge-success' : child.status === 2 ? 'badge-danger' : 'badge-muted'"
            >
              {{ child.status === 1 ? $t('instanceStatus.inService') : child.status === 2 ? $t('instanceStatus.defective') : $t('instanceStatus.retired') }}
            </span>
          </div>
        </div>
        <div class="data-card__actions" @click.stop>
          <button
            class="btn-icon"
            :title="$t('items.unassignFromContainer')"
            :disabled="unassigning === child.id"
            @click="unassignChild(child)"
          >
            <MdiLinkOff class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/store'
import { useToast } from '@/composables/useToast'
import {
  getChildInstances,
  getSearchResults,
  getItemsList,
  getAreas,
  createItemInstance,
  updateItemInstance,
} from '@/api/api.js'
import EmptyState from '@/components/base/EmptyState.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import MdiPackageVariantClosed from '~icons/mdi/package-variant-closed'
import MdiLinkOff from '~icons/mdi/link-off'

const props = defineProps({
  parentId: { type: Number, required: true },
})

const emit = defineEmits(['changed'])

const router  = useRouter()
const store   = useStore()
const toast   = useToast()
const { t }   = useI18n()

// ── children list ─────────────────────────────────────────────────────
const children = ref([])
const loadingChildren = ref(false)

async function loadChildren() {
  loadingChildren.value = true
  try {
    const res = await getChildInstances(props.parentId)
    children.value = res.results ?? res ?? []
  } catch {
    toast.error(t('items.failedLoadContained'))
  } finally {
    loadingChildren.value = false
  }
}

// ── add panel ─────────────────────────────────────────────────────────
const showAddPanel = ref(false)
const addMode = ref('search')

// ── search existing ───────────────────────────────────────────────────
const searchQuery = ref('')
const searchOpen = ref(false)
const searchLoading = ref(false)
const searchResults = ref([])
const assigning = ref(false)
let searchDebounce = null

watch(searchQuery, (val) => {
  clearTimeout(searchDebounce)
  if (val.trim().length < 2) { searchResults.value = []; return }
  searchLoading.value = true
  searchDebounce = setTimeout(async () => {
    try {
      const res = await getSearchResults(val.trim())
      const existingIds = new Set(children.value.map(c => c.id))
      existingIds.add(props.parentId)
      const items = []
      ;(res.results?.item_metadata ?? []).forEach(r => {
        if (existingIds.has(r.object_id)) return
        items.push({
          id: r.object_id,
          displayName: r.display?.name ?? `#${r.object_id}`,
          sub: r.display?.subtitle || null,
          typeLabel: t('items.title'),
        })
      })
      searchResults.value = items
    } catch {
      searchResults.value = []
    } finally {
      searchLoading.value = false
    }
  }, 280)
})

function onSearchBlur() {
  setTimeout(() => { searchOpen.value = false }, 200)
}

async function assignExisting(result) {
  assigning.value = true
  searchOpen.value = false
  searchQuery.value = ''
  searchResults.value = []
  try {
    await updateItemInstance(result.id, { parent: props.parentId })
    toast.success(t('items.addedToContainer'))
    await loadChildren()
    emit('changed')
  } catch (e) {
    toast.error(e?.data?.detail ?? t('items.failedAssignToContainer'))
  } finally {
    assigning.value = false
  }
}

// ── create new ────────────────────────────────────────────────────────
const itemTypes = ref([])
const areas = ref([])
const newChild = ref({ item: '', area: '', assetTag: '', serial_number: '' })
const creatingChild = ref(false)

async function loadCreateOptions() {
  const areaParams = store.currentWarehouse?.id ? { warehouse: store.currentWarehouse.id } : {}
  const [itemRes, areaRes] = await Promise.all([
    getItemsList().catch(() => ({ results: [] })),
    getAreas(areaParams).catch(() => ({ results: [] })),
  ])
  itemTypes.value = itemRes.results ?? itemRes ?? []
  areas.value = areaRes.results ?? areaRes ?? []
}

async function createChild() {
  if (!newChild.value.item || !newChild.value.area) return
  creatingChild.value = true
  try {
    const payload = {
      item: parseInt(newChild.value.item, 10),
      area: parseInt(newChild.value.area, 10),
      status: 1,
      parent: props.parentId,
    }
    if (newChild.value.assetTag) payload.assetTag = newChild.value.assetTag
    if (newChild.value.serial_number) payload.serial_number = newChild.value.serial_number
    await createItemInstance(payload)
    toast.success(t('items.instanceCreatedInContainer'))
    newChild.value = { item: '', area: '', assetTag: '', serial_number: '' }
    await loadChildren()
    emit('changed')
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.data?.item?.[0] ?? t('items.failedCreateInContainer'))
  } finally {
    creatingChild.value = false
  }
}

// ── un-assign ─────────────────────────────────────────────────────────
const unassigning = ref(null)

async function unassignChild(child) {
  unassigning.value = child.id
  try {
    await updateItemInstance(child.id, { parent: null })
    toast.success(t('items.removedFromContainer'))
    await loadChildren()
    emit('changed')
  } catch (e) {
    toast.error(e?.data?.detail ?? t('items.failedUnassignFromContainer'))
  } finally {
    unassigning.value = null
  }
}

// ── navigation ────────────────────────────────────────────────────────
function goToChild(child) {
  if (child.item?.item_type === 2) {
    router.push(`/item/${child.item.id}/instance/${child.id}`)
  } else if (child.item?.id) {
    router.push(`/item/${child.item.id}`)
  }
}

// ── lifecycle ─────────────────────────────────────────────────────────
onMounted(() => {
  loadChildren()
  loadCreateOptions()
})

watch(() => props.parentId, () => {
  loadChildren()
})
</script>
